import React, { useEffect, useMemo } from 'react';
import { Button, Box, CircularProgress, Typography, Tooltip } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import { useTranslation } from 'react-i18next';
import { RootState, useAppDispatch, useAppSelector } from '../../store/store';
import { GetDatasetsCosmosDB, handleToggleFileDatasetPopup } from '../../store/mltester-slice/mltester';
import Transition from '../common/Transition';
import { Header } from '../common/Header';

const TesterFileData = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    
    const { 
        fileDatasetPopupOpen, 
        fileDatasetBodyParams,
        fileDataset,           
        fileDatasetLoading     
    } = useAppSelector((state: RootState) => state.mltester);

    const [paginationModel, setPaginationModel] = React.useState({
        page: 0,
        pageSize: 10,
    });

    const handlePaginationChange = (newPaginationModel: any) => {
        setPaginationModel(newPaginationModel);  
        dispatch(GetDatasetsCosmosDB({
            datasetId: fileDatasetBodyParams.datasetId, 
            pageNumber: newPaginationModel.page, 
            pageSize: newPaginationModel.pageSize 
        }));
    };

    const generateColumns = (headers: string[]): GridColDef[] => {
        return headers.map((header) => ({
            field: header,
            headerName: header,
            flex: 1,
            minWidth: 150,
            sortable: false,
            filterable: false,
            renderHeader: () => (
               <Tooltip title={header} arrow placement="top">
                    <span style={{
                        fontWeight: 'bold',
                        fontSize: '14px',
                        color: '#333',
                        cursor: 'pointer'
                    }}>
                        {header}
                    </span>
                </Tooltip>
            ),
            renderCell: (params) => (
                <>
                    {params.value ?? '-'}                
                </>
            ),
        }));
    };

    const columns: GridColDef[] = useMemo(() => {
        if (!fileDataset?.headers) return [];
        return generateColumns(fileDataset.headers);
    }, [fileDataset?.headers]);

    function handleClose() {
        dispatch(handleToggleFileDatasetPopup({ open: false, body: null }));
    }

    useEffect(() => {
        if (fileDatasetBodyParams) {
            dispatch(GetDatasetsCosmosDB({
                datasetId: fileDatasetBodyParams.datasetId, 
                pageNumber: fileDatasetBodyParams.pageNumber,
                pageSize: fileDatasetBodyParams.pageSize
            }));
        }
    }, [fileDatasetBodyParams, dispatch]);

    if (!fileDatasetBodyParams) return <></>;

    return (
        <Dialog
            fullWidth
            maxWidth="lg"
            open={fileDatasetPopupOpen}
            TransitionComponent={Transition}
            keepMounted
            onClose={(_, reason) => {
                if (reason !== 'backdropClick') handleClose();
            }}
            aria-describedby="alert-dialog-slide-description"
        >
            <Header title={fileDataset?.name} />
            
            <DialogContent>
                {fileDatasetLoading ? (
                    <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        height: 400,
                        gap: 2
                    }}>
                        <CircularProgress size={40} />
                        <Typography variant="body1" color="text.secondary">
                            Loading dataset...
                        </Typography>
                    </Box>
                ) : !fileDataset ? (
                    <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        height: 400
                    }}>
                        <Typography variant="h6" color="text.secondary">
                            No data available
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ height: 400, width: '100%' }}>
                        <DataGrid
                            rows={fileDataset.rows?.map((row: any, index: number) => ({ 
                                ...row, 
                                id: index 
                            })) || []}
                            columns={columns}                       
                            initialState={{
                                pagination: {
                                    paginationModel: {
                                        pageSize: 10,
                                    },
                                },
                            }}
                            pageSizeOptions={[5, 10, 25]}
                            paginationMode="server"
                            rowCount={fileDataset.totalRecords || 0}
                            paginationModel={paginationModel}
                            onPaginationModelChange={handlePaginationChange}
                            rowHeight={40}
                            columnHeaderHeight={35}        
                        />
                    </Box>
                )}
            </DialogContent>
            
            <DialogActions>
                <Button 
                    className="button-text-color" 
                    onClick={handleClose}
                >
                    {t('CLOSE_BUTTON')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default TesterFileData;